import { ArrowRight, FileCheck2, Link as LinkIcon } from 'lucide-react';
import { SmartLink } from '../ui/SmartLink';

/**
 * Inline action card in an assistant message: a ```firmio-link block carrying
 * a title, an optional subtitle and one button that goes somewhere in the app
 * (the draft invoice to approve, a task, a page). A tool returns the block as
 * `chatBlock`; the model pastes it. On a phone a big button beats a markdown
 * link the model may wrap in bold or that wraps across three lines.
 */

export interface LinkCardSpec {
  title: string;
  url: string;
  label?: string | null;
  subtitle?: string | null;
  /** Picks the icon: 'invoice' for invoices, anything else a link. */
  kind?: string | null;
}

export function parseLinkSpec(text: string): LinkCardSpec | null {
  try {
    const raw = JSON.parse(text) as Record<string, unknown>;
    if (!raw || typeof raw !== 'object') return null;
    if (typeof raw.title !== 'string' || typeof raw.url !== 'string' || !raw.url.trim())
      return null;
    return {
      title: raw.title,
      url: raw.url,
      label: typeof raw.label === 'string' ? raw.label : null,
      subtitle: typeof raw.subtitle === 'string' ? raw.subtitle : null,
      kind: typeof raw.kind === 'string' ? raw.kind : null,
    };
  } catch {
    return null;
  }
}

export function ChatLinkCard({ spec }: { spec: LinkCardSpec }) {
  const Icon = spec.kind === 'invoice' ? FileCheck2 : LinkIcon;
  return (
    <div className="my-1 flex flex-wrap items-center gap-3 rounded-lg border border-border bg-muted/40 px-3 py-2.5">
      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-primary/10 text-primary">
        <Icon className="h-5 w-5" />
      </div>
      <div className="min-w-0 flex-1 basis-40">
        <p className="break-words text-sm font-medium text-foreground">{spec.title}</p>
        {spec.subtitle ? (
          <p className="break-words text-xs text-muted-foreground">{spec.subtitle}</p>
        ) : null}
      </div>
      <SmartLink
        href={spec.url}
        className="inline-flex shrink-0 items-center gap-1.5 rounded-md bg-primary px-3 py-2 text-xs font-medium text-primary-foreground hover:opacity-90"
      >
        {spec.label ?? 'Deschide'}
        <ArrowRight size={14} />
      </SmartLink>
    </div>
  );
}
